import {Platform} from 'react-native';
import Toast from 'react-native-toast-message';

import {applePayService} from '../../../components/payments/till/PaymentMethodSelector/applePay';
import type {ApplePayPayment} from '../../../components/payments/till/PaymentMethodSelector/applePay/types';
import {ERROR_MESSAGE} from '../../../components/ToastBottom';
import type {CartStore} from '../../../stores/cartStore';
import {logger, serializeError} from '../../../utils/logger';
import {getNativeBrowserData, getWeb3DsBrowserData} from '../util/threeDS';

export interface ApplePayPaymentHandlerParams {
  /** Cart store for checkout */
  cartStore: CartStore;
  /** Log tag for identifying source */
  logTag: string;
  /** Source platform */
  source: 'web' | 'ios';
}

/**
 * Shared Apple Pay payment handler for both web and iOS.
 * Uses cartStore.checkoutOrder with single_use_payment_token.
 *
 * @returns Function that processes an Apple Pay payment
 */
export function createApplePayPaymentHandler({
  cartStore,
  logTag,
  source,
}: ApplePayPaymentHandlerParams) {
  return async (payment: ApplePayPayment): Promise<boolean> => {
    // Validate token
    if (!payment?.token?.paymentData) {
      logger.error(logTag, 'Apple Pay payment failed - invalid token');
      Toast.show({
        type: ERROR_MESSAGE,
        text1: 'Payment authorization failed. Please try again.',
      });
      return false;
    }

    logger.info(logTag, `${source} Apple Pay payment authorized`, {
      network: payment.token.paymentMethod.network,
      displayName: payment.token.paymentMethod.displayName,
    });

    try {
      // Build single_use_payment_token for checkout API
      const singleUsePaymentToken = applePayService.buildPaymentToken(payment);

      // Get browser data (web: actual browser data, mobile: device info)
      const browserData = Platform.OS === 'web' ? getWeb3DsBrowserData() : getNativeBrowserData();

      // Use cartStore.checkoutOrder - handles validation, navigation, error display
      const succeeded = await cartStore.checkoutOrder(
        undefined, // Navigation handled internally by cartStore via RootNavigation
        browserData,
        undefined, // No payment_instrument_id for Apple Pay
        singleUsePaymentToken,
      );

      logger.info(logTag, `${source} Apple Pay checkout submitted`, {succeeded});
      return succeeded === true;
    } catch (error) {
      logger.error(logTag, `${source} Apple Pay payment error`, serializeError(error));
      // cartStore.checkoutOrder handles error toasts
      return false;
    }
  };
}
